import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import CancelIcon from '@mui/icons-material/Cancel';
import { Column } from '../../../components/table/DataTable';
import { DataTableRef } from '../../../components/table/dataTableRef';
import DataTable from '../../../components/table/DataTable';
import { OrganizationDto } from '../../../services/Payment/paymentService.dto';
import { useOrganizationFilters } from '../hooks/useOrganizationFilters';
import { usePopup } from '../../../providers/PopupProvider';
import { useError } from '../../../providers/useError';
import { paymentService } from '../../../services/Payment/paymentService';
import InputWithLabel from '../../../components/inputs/InputWithLabel';
import RectangularButton from '../../../components/buttons/RectangularButton';

const FilterContainer = styled.div`
  flex-direction: row;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-top: 10px;
  gap: 5px;
`;

const ApplyButton = styled(RectangularButton)`
  min-width: 80px;
  font-weight: normal;
  height: 30px;
  margin: 5px;
`;

const ClearButton = styled(RectangularButton)`
  min-width: 80px;
  font-weight: normal;
  height: 30px;
  margin: 5px;
`;

const StyledButton = styled.div`
  cursor: pointer;
  display: flex;
  justify-self: center;
`;

const iconStyle = {
  fontSize: 20,
  cursor: 'pointer',
};

const OrganizationsTable = () => {
  const { t } = useTranslation('manageContent');
  const dataTableRef = useRef<DataTableRef>(null);
  const { onTableFiltered, filterInitState } = useOrganizationFilters();
  const [filterData, setFilterData] = useState<Map<string, any>>(filterInitState || new Map());
  const [keywords, setKeywords] = useState<string>(filterInitState?.get('keywords') || '');
  const [ein, setEin] = useState<string>(filterInitState?.get('ein') || '');
  const { showConfirmPopover, showSuccessPopover } = usePopup();
  const { handleError } = useError();

  const handleDelete = (ein: string) => {
    showConfirmPopover(t('organizations.delete.confirm', { ein }), () => {
      paymentService
        .deleteSingle(ein)
        .then(() => {
          dataTableRef.current?.refresh();
          showSuccessPopover(t('organizations.delete.success'));
        })
        .catch((error: AxiosError) => {
          handleError(error, t('organizations.delete.error'));
        });
    });
  };

  const columns: Column<OrganizationDto>[] = [
    {
      id: 'id',
      title: t('organizations.table.columns.id'),
      isSortable: true,
      maxWidth: 80,
      align: 'center',
      renderCell: data => data.id,
    },
    {
      id: 'ein',
      title: t('organizations.table.columns.ein'),
      isSortable: true,
      maxWidth: 120,
      renderCell: data => data.ein,
    },
    {
      id: 'name',
      title: t('organizations.table.columns.name'),
      isSortable: true,
      renderCell: data => data.name,
    },
    {
      id: 'address',
      title: t('organizations.table.columns.address'),
      renderCell: data => {
        const addr = data.address;
        return `${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`;
      },
    },
    {
      id: 'actions',
      title: t('organizations.table.columns.actions'),
      align: 'center',
      maxWidth: 100,
      renderCell: data => (
        <StyledButton onClick={() => handleDelete(data.ein)}>
          <CancelIcon style={iconStyle} />
        </StyledButton>
      ),
    },
  ];

  const handleApply = () => {
    const newFilterData = new Map<string, any>();
    if (keywords.trim()) {
      newFilterData.set('keywords', keywords.trim());
    }
    if (ein.trim()) {
      newFilterData.set('ein', ein.trim());
    }
    setFilterData(newFilterData);
  };

  const handleClear = () => {
    setKeywords('');
    setEin('');
    const emptyFilterData = new Map<string, any>();
    setFilterData(emptyFilterData);
    setTimeout(() => {
      dataTableRef.current?.refresh();
    }, 0);
  };

  return (
    <>
      <FilterContainer>
        <InputWithLabel
          label={t('organizations.table.filters.keywords')}
          value={keywords}
          onChange={setKeywords}
          minWidth={400}
        />
        <InputWithLabel label={t('organizations.table.filters.ein')} value={ein} onChange={setEin} />
        <ApplyButton label={t('common:apply')} onClick={handleApply} />
        <ClearButton label={t('common:clear')} onClick={handleClear} />
      </FilterContainer>
      <DataTable
        ref={dataTableRef}
        columns={columns}
        filterData={filterData}
        onTableStateChanged={onTableFiltered}
        skipInitialFetch={false}
      />
    </>
  );
};

export default OrganizationsTable;
